import React, { useState } from "react";
import { motion } from "framer-motion";
import { SteeringWheel, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { useLang } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { Reveal } from "./Reveal";

const empty = { name: "", phone: "", email: "", city: "", state: "", experience: "", has_car: "", notes: "" };

export const DriverForm = () => {
  const { t } = useLang();
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await api.post("/drivers", form);
      setStatus("success");
      setForm(empty);
      setTimeout(() => setStatus("idle"), 6000);
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 5000);
    }
  };

  const inputCls = "w-full bg-white/5 border border-white/15 text-white px-4 py-3.5 rounded-none outline-none focus:border-taxi focus:ring-1 focus:ring-taxi placeholder:text-white/30 transition-colors";
  const labelCls = "block text-taxi/80 text-xs font-bold uppercase tracking-widest mb-2";

  return (
    <section id="drive" className="relative bg-black py-24 md:py-36 overflow-hidden grid-lines" data-testid="driver-section">
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-taxi/10 blur-3xl pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-10 bg-taxi" />
                <span className="text-taxi text-xs md:text-sm font-bold uppercase tracking-[0.25em]">{t("drive_kicker")}</span>
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <div className="flex items-center gap-4 mb-4">
                <SteeringWheel size={48} weight="fill" className="text-taxi shrink-0" />
                <h2 className="font-display text-white text-5xl md:text-7xl uppercase leading-[0.88]">{t("drive_title")}</h2>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-md">{t("drive_desc")}</p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="border border-taxi/30 bg-[#0d0d0d] p-6 md:p-10" data-testid="driver-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>{t("df_name")}</label>
                    <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="driver-name" placeholder="John Doe" />
                  </div>
                  <div>
                    <label className={labelCls}>{t("df_phone")}</label>
                    <input required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="driver-phone" placeholder="+1 ..." />
                  </div>
                  <div>
                    <label className={labelCls}>{t("df_email")}</label>
                    <input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="driver-email" placeholder="you@email.com" />
                  </div>
                  <div>
                    <label className={labelCls}>{t("df_experience")}</label>
                    <input value={form.experience} onChange={set("experience")} className={inputCls} data-testid="driver-experience" placeholder="5" />
                  </div>
                  <div>
                    <label className={labelCls}>{t("df_city")}</label>
                    <input value={form.city} onChange={set("city")} className={inputCls} data-testid="driver-city" placeholder="..." />
                  </div>
                  <div>
                    <label className={labelCls}>{t("df_state")}</label>
                    <input value={form.state} onChange={set("state")} className={inputCls} data-testid="driver-state" placeholder="NY" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>{t("df_car")}</label>
                    <select value={form.has_car} onChange={set("has_car")} className={inputCls} data-testid="driver-car">
                      <option value="">—</option>
                      <option value={t("df_yes")}>{t("df_yes")}</option>
                      <option value={t("df_no")}>{t("df_no")}</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>{t("df_notes")}</label>
                    <textarea rows="3" value={form.notes} onChange={set("notes")} className={inputCls} data-testid="driver-notes" placeholder="..." />
                  </div>
                </div>

                <motion.button whileTap={{ scale: 0.98 }} type="submit" disabled={status === "sending"}
                  className="mt-7 w-full bg-taxi text-black font-bold text-base py-4 rounded-full hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  data-testid="driver-submit">
                  {status === "sending" ? t("df_sending") : t("df_submit")}
                  {status !== "sending" && <ArrowRight size={18} weight="bold" />}
                </motion.button>

                {status === "success" && (
                  <div className="mt-4 flex items-center gap-2 text-[#25D366] font-semibold" data-testid="driver-success">
                    <CheckCircle size={20} weight="fill" /> {t("df_success")}
                  </div>
                )}
                {status === "error" && (
                  <div className="mt-4 text-usared font-semibold" data-testid="driver-error">{t("df_error")}</div>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
